import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { AspectRatio } from '@lumen5/luminary';

import type { ChatUploadDropdownItem, ChatUploadDropdownRef } from './ChatUploadDropdown';
import { ChatUploadDropdown } from './ChatUploadDropdown';

import type { AIVoice, Blueprint, Template } from 'js/src/client';
import { NarrativeThreadEnum, Urls } from 'js/src/client';
import type {
    ClearableSketchControl,
    DesignReference,
    DesignReferenceKind,
    SketchControl,
} from 'js/src/components/sketch/SketchControls';
import { SketchControls } from 'js/src/components/sketch/SketchControls';
import type { SketchControlsBadgesState } from 'js/src/components/sketch/SketchControlsBadges';
import { getBlueprint } from 'js/src/data/blueprints/blueprints-selectors';
import { getAllBrandKits, getBrandKitColorsInHex, loadBrandKits } from 'js/src/data/brand-kits';
import {
    resetMediaSourcesToDefaultsOperation,
    selectDamMediaEnabled,
    selectMediaGuidance,
    selectStockMediaEnabled,
    selectWorkspaceMediaMode,
} from 'js/src/data/media-recommendation-preferences';
import { saveSketchControlsToCookie } from 'js/src/data/sketch-controls/sketch-controls-cookies';
import {
    selectSketchControls,
    selectSketchControlsBlueprint,
    selectSketchControlsBlueprintName,
    selectSketchControlsBrandKitId,
    selectSketchControlsBrandKitName,
    selectSketchControlsTemplate,
    selectSketchControlsVoiceName,
} from 'js/src/data/sketch-controls/sketch-controls-selectors';
import {
    clearAspectRatio,
    clearBlueprint,
    clearBrandKit,
    clearDuration,
    clearNarrativeThread,
    clearTemplate,
    clearVoice,
    setAspectRatio,
    setBlueprint,
    setBrandKit,
    setDuration,
    setNarrativeThread,
    setTemplate,
    setVoice,
} from 'js/src/data/sketch-controls/sketch-controls-slice';
import { clearIncompatibleSketchControls } from 'js/src/data/sketch-controls/sketch-controls-validation';
import {
    getSupportedAspectRatios,
    getTemplatePaletteColorCodes,
} from 'js/src/data/templates/templates-selectors';
import type { PropertyChange } from 'js/src/data/video-planner/video-plan-compatibility';
import type { ImmutableMap } from 'js/src/libs/consts';
import { int2rgb } from 'js/src/libs/css-helpers';
import Flags from 'js/src/libs/flags';
import Navigation from 'js/src/libs/navigation';
import { type RootState, useAppDispatch } from 'js/src/libs/RootState';

interface ChatDropdownProps {
    onFilesSelected: (files: File[]) => void;
    disabled?: boolean;
    className?: string;
    canAttachFiles?: boolean;
    showSketchControls?: boolean;
    canUseAIVoiceover?: boolean;
    hideBlueprint?: boolean;
    matchedReference?: DesignReference | null;
    /** When provided, the attach menu offers a "Choose from your uploads"
     * option that opens a media-library picker. */
    onChooseExisting?: () => void;
    /** Extra items appended to the "+" attach menu. */
    attachMenuItems?: ChatUploadDropdownItem[];
    /** Extra controls rendered in the same row, after the settings button. */
    children?: React.ReactNode;
    /** Exposes the attach menu so a caller can pop it open. */
    attachMenuRef?: React.Ref<ChatUploadDropdownRef>;
}

export const ChatDropdown: React.FC<ChatDropdownProps> = ({
    onFilesSelected,
    disabled = false,
    className = '',
    canAttachFiles = true,
    showSketchControls = false,
    canUseAIVoiceover = true,
    hideBlueprint = false,
    matchedReference = null,
    onChooseExisting,
    attachMenuItems,
    children,
    attachMenuRef,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dispatch = useAppDispatch();
    const controlsState = useSelector(selectSketchControls);
    const selectedTemplate = useSelector(selectSketchControlsTemplate);
    const blueprintName = useSelector(selectSketchControlsBlueprintName);
    const voiceName = useSelector(selectSketchControlsVoiceName);

    const designSetBy: DesignReferenceKind | null =
        matchedReference?.kind ?? (controlsState?.blueprintId ? 'blueprint' : null);
    // Known for blueprints only; a matched past video's thread isn't loaded here.
    const designBlueprint = useSelector((state: RootState) =>
        matchedReference
            ? matchedReference.kind === 'blueprint'
                ? getBlueprint(state, Number(matchedReference.id))
                : null
            : selectSketchControlsBlueprint(state),
    );
    const designHasNoVoiceover = designBlueprint?.narrative_thread === NarrativeThreadEnum.TEXT;

    const supportedAspectRatios = useSelector((state: RootState) =>
        controlsState?.templateId
            ? getSupportedAspectRatios(state, controlsState.templateId)
            : null,
    );

    const stockMediaEnabled = useSelector(selectStockMediaEnabled);
    const damMediaEnabled = useSelector(selectDamMediaEnabled);
    const workspaceMediaMode = useSelector(selectWorkspaceMediaMode);
    const mediaGuidance = useSelector(selectMediaGuidance);
    const isDamAvailable = Flags.canSeeDamTab();
    const brandKitName = useSelector(selectSketchControlsBrandKitName);
    const selectedBrandKitId = useSelector(selectSketchControlsBrandKitId);
    const brandKitColors = useSelector((state: RootState) =>
        selectedBrandKitId ? getBrandKitColorsInHex(state, selectedBrandKitId) : null,
    );
    const templatePaletteColors = useSelector((state: RootState) =>
        controlsState?.templateId
            ? getTemplatePaletteColorCodes(state, controlsState.templateId)
            : null,
    );
    const brandKits = useSelector(getAllBrandKits);

    React.useEffect(() => {
        if (showSketchControls) {
            dispatch(loadBrandKits());
        }
    }, [showSketchControls, dispatch]);

    const handleFileChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(event.target.files || []);
            if (files.length > 0) {
                onFilesSelected(files);
            }
            event.target.value = '';
        },
        [onFilesSelected],
    );

    const clearIncompatibleSettings = useCallback(
        (change: PropertyChange) => {
            dispatch(clearIncompatibleSketchControls(change));
        },
        [dispatch],
    );

    const handleControlSelected = useCallback(
        (control: SketchControl, value?: unknown) => {
            switch (control) {
                case 'blueprint':
                case 'template':
                case 'duration':
                case 'format':
                case 'voice':
                case 'mediaSources':
                    // These controls are handled by SketchControls directly
                    break;
                case 'narrativeThread': {
                    const narrativeThread = value as NarrativeThreadEnum;
                    dispatch(setNarrativeThread(narrativeThread));

                    if (narrativeThread === NarrativeThreadEnum.TEXT) {
                        dispatch(clearVoice());
                    }
                    dispatch(saveSketchControlsToCookie());
                    break;
                }
                case 'brandKit': {
                    const brandKitId = value as number;

                    // If brandKitId is -1, open create brand kit page in new tab
                    if (brandKitId === -1) {
                        Navigation.openNewTab(Urls.BRAND_KITS);
                        return;
                    }

                    dispatch(setBrandKit(brandKitId));
                    dispatch(saveSketchControlsToCookie());
                    break;
                }
                default: {
                    const _exhaustive: never = control;
                    throw new Error(`Unhandled control: ${_exhaustive}`);
                }
            }
        },
        [dispatch],
    );

    const handleSelectDuration = useCallback(
        (seconds: number) => {
            dispatch(setDuration(seconds));
            dispatch(saveSketchControlsToCookie());
        },
        [dispatch],
    );

    const handleSelectAspectRatio = useCallback(
        (aspectRatio: AspectRatio) => {
            dispatch(setAspectRatio(aspectRatio));
            clearIncompatibleSettings({ type: 'aspectRatio', newValue: aspectRatio });
            dispatch(saveSketchControlsToCookie());
        },
        [dispatch, clearIncompatibleSettings],
    );

    const handleSelectBlueprint = useCallback(
        (blueprint: Blueprint) => {
            dispatch(setBlueprint(blueprint.id));
            if (blueprint.default_target_duration_seconds) {
                dispatch(setDuration(blueprint.default_target_duration_seconds));
            }
            dispatch(saveSketchControlsToCookie());
        },
        [dispatch],
    );

    const handleSelectTemplate = useCallback(
        (template: ImmutableMap<Template>) => {
            dispatch(setTemplate(template.get('id')));
            clearIncompatibleSettings({ type: 'template', newValue: template });
            dispatch(saveSketchControlsToCookie());
        },
        [clearIncompatibleSettings, dispatch],
    );

    const handleSelectVoice = useCallback(
        (voice: AIVoice) => {
            dispatch(setVoice(voice.slug));
            // A design reference fixes the thread; setting it would drop a selected blueprint.
            if (!designSetBy) {
                dispatch(setNarrativeThread(NarrativeThreadEnum.VOICEOVER));
            }
            dispatch(saveSketchControlsToCookie());
        },
        [designSetBy, dispatch],
    );

    const handleClearBlueprint = useCallback(() => {
        dispatch(clearBlueprint());
        dispatch(saveSketchControlsToCookie());
    }, [dispatch]);

    const handleClearTemplate = useCallback(() => {
        dispatch(clearTemplate());
        dispatch(saveSketchControlsToCookie());
    }, [dispatch]);

    const handleClearNarrativeThread = useCallback(() => {
        dispatch(clearNarrativeThread());
        dispatch(clearVoice());
        dispatch(saveSketchControlsToCookie());
    }, [dispatch]);

    const handleClearDuration = useCallback(() => {
        dispatch(clearDuration());
        dispatch(saveSketchControlsToCookie());
    }, [dispatch]);

    const handleClearAspectRatio = useCallback(() => {
        dispatch(clearAspectRatio());
        dispatch(saveSketchControlsToCookie());
    }, [dispatch]);

    const handleClearBrandKit = useCallback(() => {
        dispatch(clearBrandKit());
        dispatch(saveSketchControlsToCookie());
    }, [dispatch]);

    const handleClearMediaSources = useCallback(() => {
        dispatch(resetMediaSourcesToDefaultsOperation());
    }, [dispatch]);

    const handleClearControl = useCallback(
        (control: ClearableSketchControl) => {
            switch (control) {
                case 'blueprint':
                    handleClearBlueprint();
                    break;
                case 'template':
                    handleClearTemplate();
                    break;
                case 'narrativeThread':
                    handleClearNarrativeThread();
                    break;
                case 'duration':
                    handleClearDuration();
                    break;
                case 'format':
                    handleClearAspectRatio();
                    break;
                case 'mediaSources':
                    handleClearMediaSources();
                    break;
                case 'brandKit':
                    handleClearBrandKit();
                    break;
                default: {
                    const _exhaustive: never = control;
                    throw new Error(`Unhandled control: ${_exhaustive}`);
                }
            }
        },
        [
            handleClearAspectRatio,
            handleClearBlueprint,
            handleClearBrandKit,
            handleClearDuration,
            handleClearMediaSources,
            handleClearNarrativeThread,
            handleClearTemplate,
        ],
    );

    const firstTemplatePalette = templatePaletteColors && templatePaletteColors[0];
    const templateColors = firstTemplatePalette
        ? firstTemplatePalette.map((intColor) => `rgb(${int2rgb(intColor).join(',')})`)
        : null;
    const brandKitColorsArray = brandKitColors ? brandKitColors.toArray() : null;

    const badgesState: SketchControlsBadgesState | null = showSketchControls
        ? {
              blueprintName: blueprintName,
              templateName: selectedTemplate?.get('display_name') || null,
              templateColors: brandKitName ? null : templateColors,
              narrativeThread:
                  controlsState?.narrativeThread ||
                  (designSetBy && controlsState?.voiceId ? NarrativeThreadEnum.VOICEOVER : null),
              durationSeconds: controlsState?.durationSeconds || null,
              aspectRatio: controlsState?.aspectRatio || null,
              voiceName: voiceName,
              mediaSources: {
                  stockMediaEnabled,
                  damMediaEnabled,
                  workspaceMediaMode,
                  mediaGuidance,
                  isDamAvailable,
              },
              brandKitName: brandKitName,
              brandKitColors: brandKitColorsArray,
          }
        : null;

    return (
        <div className={className}>
            {canAttachFiles && (
                <ChatUploadDropdown
                    ref={attachMenuRef}
                    disabled={disabled}
                    uploadType="all"
                    onFilesSelected={onFilesSelected}
                    onChooseExisting={onChooseExisting}
                    extraItems={attachMenuItems}
                />
            )}
            {showSketchControls && badgesState && (
                <SketchControls
                    source="chat"
                    dropdownDisabled={disabled}
                    badgesState={badgesState}
                    hideBlueprint={hideBlueprint}
                    designSetBy={designSetBy}
                    designHasNoVoiceover={designHasNoVoiceover}
                    selectedTemplateId={controlsState?.templateId ?? null}
                    selectedAIVoiceId={controlsState?.voiceId ?? null}
                    canUseAIVoiceover={canUseAIVoiceover}
                    brandKits={brandKits}
                    supportedAspectRatios={supportedAspectRatios}
                    onSelectControl={handleControlSelected}
                    onSelectBlueprint={handleSelectBlueprint}
                    onSelectTemplate={handleSelectTemplate}
                    onSelectVoice={handleSelectVoice}
                    onSelectDuration={handleSelectDuration}
                    onSelectAspectRatio={handleSelectAspectRatio}
                    onClearControl={handleClearControl}
                />
            )}
            {children}
            {canAttachFiles && (
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.docx,.pptx,.txt,.xml,.png,.jpg,.jpeg,.bmp,.webp,.gif,.mov,.mp4,.avi,.webm,.m4v,.mp3,.wav,.m4a,.aac,.ogg"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
            )}
        </div>
    );
};
